import React, { useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import CustomModal from '../../components/Modal';
import { Button, FormFeedback, FormGroup, Input, InputGroup, Spinner } from 'reactstrap';
import { WithRecaptcha } from '../../containers/WithRecaptcha';
import { isInvalidCredentialsError, isMaxAttemptsError, isMaxLoginAttemptsError, validateEmail } from '../../utils';
import { useAuth } from '../../context/AuthContext';
import ManageError from './ManageError';
import CustomMaterialButton from '../../components/CustomButton/MaterialButton';

const LoginForm = (props) => {
  const { setUserAuth } = useAuth();
  const parameters = queryString.parse(props.location.search);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [customUsernameError, setCustomUsernameError] = useState();
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [restrictiveError, setRestrictiveError] = useState(null);

  const registerUserUrl = parameters?.cp_convert === 'dtvgo'
    ? window.configOptions.TBX_SKY_DTVGO_REGISTER_USER
    : window.configOptions.TBX_SKY_REGISTER_USER;

  const IconSvg = () => <img src="/img/sky/alert-modal.svg" />;

  const goBackToLoginForm = () => {
    setModalError(false);
  };

  useEffect(() => {
    setUserAuth(null);
  }, []);

  const handleApiErrors = (error) => {
    if (isMaxAttemptsError(error)) {
      setModalError({
        title: isMaxLoginAttemptsError(error)
          ? 'Você esgotou as tentativas pra entrar na sua conta agora. Aguarde 1h para tentar de novo.'
          : 'Você esgotou as tentativas de envio. Aguarde 1h para tentar de novo.',
        buttonText: 'Ok, entendi',
        onButtonClick: () => goBackToLoginForm()
      });
    } else if (error instanceof props.api.SkyInterruptedAuthenticationError) {
      setRestrictiveError(error);
    } else {
      setRestrictiveError({ ...error, customData: { defaultSkyError: true } });
    }
  };


  const passwordLessErrorHandler = (error) => {
    if (isInvalidCredentialsError(error)) {
      if (!username) {
        setCustomUsernameError('Digite o e-mail ou celular valido pra continuar.');
      }
      if (validateEmail(username)) {
        setCustomUsernameError('Este e-mail não tem assinatura SKY');
      } else {
        setCustomUsernameError('Este celular não tem assinatura SKY');
      }
    } else {
      handleApiErrors(error);
    }
  };

  const handleSubmitErrors = (error) => {
    setLoading(false);
    setUserAuth(null);
    if (isInvalidCredentialsError(error)) {
      setInvalidPassword(true);
      setCustomUsernameError('Digite o e-mail ou celular valido pra continuar.');
      setInvalidUsername(true);
    } else {
      handleApiErrors(error);
    }
  };

  const loginFlow = () => {
    props.api.userPassLogin(username, password, parameters)
      .then(async (response) => {
        setLoading(false);
        window.location = parameters.redirect_uri + '?' + queryString.stringify(response);
      }).catch(error => {
        handleSubmitErrors(error);
      });
  };

  const passwordLessFlow = () => {
    if (!username) {
      setInvalidUsername(true);
    } else {
      setInvalidUsername(false);
      props.setUsername(username);
      props.api.createOtpCode({ username }).then(_ => {
        props.setIsValidPasswordLessFlow(true);
      }).catch(error => {
        setLoading(false);
        setInvalidUsername(true);
        passwordLessErrorHandler(error);
      });
    }
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    setLoading(true);
    loginFlow();
  };

  const validateCPF_CNPJ = (username) => {
    const re = /^[\d-]{8,12}$/;
    return re.test(username);
  };

  const validateUsername = (username) => {
    if (username === null) {
      return;
    }

    if (username !== '' && (validateEmail(username) || validateCPF_CNPJ(username))) {
      setInvalidUsername(false);
    } else {
      setInvalidUsername(true);
    }
  };

  const validatePassword = (password) => {
    if (password === '') {
      setInvalidPassword(true);
    } else {
      setInvalidPassword(false);
    }
  };

  const isContinueDisabled = username === null || password === null || invalidUsername || invalidPassword || loading;

  if (restrictiveError) {
    return (
      <ManageError error={restrictiveError} setError={setRestrictiveError} />
    );
  }
  return (
    <div>
      {modalError && (
        <CustomModal
          isOpen={!!modalError}
          toggle={() => setModalError(false)}
          title={modalError.title}
          Icon={IconSvg}
          buttonText={modalError.buttonText || 'Cerrar'}
          CustomMaterialButtonComp={
            <CustomMaterialButton
              materialIcon={'close'}
              goBack={goBackToLoginForm}
              containerClassName={'close-button'}
              iconClassName={'close-icon'}
            />
          }
          onButtonClick={modalError.onButtonClick}
        />
      )}
      <form onSubmit={onSubmit}>
        <div className="sky-container form-container">
          <img className="logo-sky" src="https://www.sky.com.br/o/sky-partner-modal-theme/images/logo-sky.svg" />
          <h3 className="title-sky">Entre com sua conta SKY</h3>
          <p className="instructions normal-font">
            Informe o celular ou e-mail que você cadastrou.
          </p>
          <FormGroup>
            <Input
              autoComplete="off"
              autoCorrect="off"
              placeholder="Digite o e-mail ou celular."
              invalid={invalidUsername}
              value={username === null ? '' : username}
              onBlur={() => {
                validateUsername(username);
                validatePassword(password);
              }}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
            <FormFeedback>{
              customUsernameError ? customUsernameError : 'Digite o e-mail ou celular valido pra continuar.'}
            </FormFeedback>
          </FormGroup>
          <InputGroup className="password-input">
            <Input
              autoComplete="off"
              autoCorrect="off"
              placeholder="Digite sua senha."
              invalid={invalidPassword}
              type={showPassword ? 'text' : 'password'}
              value={password === null ? '' : password}
              onBlur={() => {
                validateUsername(username);
                validatePassword(password);
              }}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <Button
              className={`btn-link ${invalidPassword ? 'invalid' : ''}`}
              onClick={() => {
                setShowPassword(!showPassword);
              }}
            >
              {showPassword ? (<i className="bi bi-eye"></i>) : (<i className="bi bi-eye-slash"></i>)}
            </Button>
            <FormFeedback>Digite uma senha válida pra continuar.</FormFeedback>
          </InputGroup>
          <p className="recover-user-instructions">
            <a className="text-bold" onClick={passwordLessFlow}> Entrar sem senha </a>
          </p>
          {error ? <div className="basic-error"> {error.message} </div> : null}
          <div className="continue-button-container">
            <Button color="primary" block disabled={isContinueDisabled}>
              {loading ? (<><Spinner size="sm">Carregande...</Spinner> Carregande...</>) : 'Continuar'}
            </Button>
          </div>
          <p className="create-user-instructions normal-font">
            Se você ainda não tem a sua conta SKY, é só <br />
            criar uma.
          </p>
          <a className="btn btn-light btn-block" href={registerUserUrl}>
            Criar conta
          </a>
        </div>
      </form>
    </div>
  );
};

export default WithRecaptcha(LoginForm);
